import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hasPermission } from '../../../services/permissions/permissions.service';
import { createQueryString } from '../../../utilities/utilities';
import { AlertHandler, ErrorAlert } from '../../../components/react/alert_handler/alert_handler';
import classNames from 'classnames';
import ConfirmationModal from '../confirmation_modal/confirmation_modal';
import { trackActivity } from '../../../services/activity/activity.api.service';

const alertHandler = new AlertHandler();
const REGISTER_PATH = '/register';
const CURRENT_PATHNAME = window.location.pathname;

const CAqueryParams: string = createQueryString({
  payment_page_permalink: 'ca-trial-downloadable',
  return_to: CURRENT_PATHNAME,
});

interface InterpolatedData {
  requiredPrivilegeConstantName: string | null;
  title: string;
  id: string;
}

interface Error {
  errors: Array<{ message: string }>;
  code: string;
}

interface PermissionRequestResponse {
  data: boolean;
  isLoading: boolean;
  error: null | Error;
}

const DownloadableButton: React.FC<InterpolatedData> = ({
  requiredPrivilegeConstantName,
  title,
  id,
}) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const DOWNLOAD_URL = `/downloadables/${id}/download`;

  const onDownloadButtonClick = (): void => {
    trackActivity({
      activity: 'download',
      target_id: `${id}`,
      target_type: 'Downloadable',
    });
    setShowConfirmationModal(true);
  };

  const renderConfirmationModal: JSX.Element = (
    <ConfirmationModal
      showConfirmationModal={showConfirmationModal}
      setShowConfirmationModal={setShowConfirmationModal}
      title={title}
    />
  );

  // If the report can be downloaded by all logged-in users, directly display the Download button
  if (!requiredPrivilegeConstantName) {
    return (
      <>
        <a
          data-testid='download-report-link'
          href={DOWNLOAD_URL}
          target='_blank'
          onClick={onDownloadButtonClick}
          className='btn btn-primary'
          rel='noreferrer'
        >
          Download the report
        </a>
        {showConfirmationModal && renderConfirmationModal}
      </>
    );
  }

  const registerPathWithReturnTo = `${REGISTER_PATH}/${
    requiredPrivilegeConstantName.includes('PRO') ? 'PRO' : 'CA'
  }${CAqueryParams}`;

  const {
    data: isAllowedToDownload,
    isLoading,
    error,
  }: PermissionRequestResponse = useQuery({
    queryKey: ['downloadablePrivileges', requiredPrivilegeConstantName],
    queryFn: () => hasPermission(requiredPrivilegeConstantName),
    onError: (err: Error) => {
      const message = Object.keys(err).includes('errors') ? err.errors[0].message : err;
      alertHandler.render(ErrorAlert, `An error occured: ${message}. Please try again later.`);
    },
    retry: false,
  });

  const renderUpgradeLink: JSX.Element = (
    <a href={registerPathWithReturnTo} className='btn btn-primary' data-testid='upgrade-link'>
      Upgrade to download
    </a>
  );

  const renderDownloadLink: JSX.Element = (
    <a
      data-testid='download-report-link'
      href={DOWNLOAD_URL}
      target='_blank'
      onClick={onDownloadButtonClick}
      className={classNames('btn btn-primary', {
        'downloadable-details-header-blur': isLoading,
        'pe-none': isLoading || Boolean(error),
      })}
      rel='noreferrer'
    >
      Download the report
    </a>
  );

  const renderRestrictedDownloadMention: JSX.Element = (
    <div data-testid='restricted-report-explanation'>
      <div className='col text-center text-white mt-4'>
        This report is available exclusively for
        {requiredPrivilegeConstantName.includes('PRO') ? ' PRO ' : ' Career '}
        account members.
      </div>
    </div>
  );

  return !error && !isAllowedToDownload && !isLoading ? (
    <>
      {renderUpgradeLink}
      {renderRestrictedDownloadMention}
    </>
  ) : (
    <>
      {renderDownloadLink}
      {showConfirmationModal && renderConfirmationModal}
    </>
  );
};

export default DownloadableButton;
